import { FC, useMemo, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { MobileListItem, Variable } from '@mobileTypes'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { VariableCreateDialog } from '@mobileComponents/Variables'

interface PrefilledVariableDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialVariableInternalId?: string
  initialRequired?: boolean
  localVariables: Variable[]
  selectedVariableInternalIds: string[]
  onClose: () => void
  onSave: (variableInternalId: string, required: boolean) => void
  onCreateVariable: (variable: Variable) => void
}

const PrefilledVariableDialog: FC<PrefilledVariableDialogProps> = ({
  open,
  mode,
  initialVariableInternalId,
  initialRequired,
  localVariables,
  selectedVariableInternalIds,
  onClose,
  onSave,
  onCreateVariable,
}) => {
  const [selectedVariable, setSelectedVariable] = useState<Variable | null>(
    initialVariableInternalId ? localVariables.find(v => v.internalId === initialVariableInternalId) || null : null
  )
  const [required, setRequired] = useState(initialRequired || false)
  const [showVariableCreateDialog, setShowVariableCreateDialog] = useState(false)

  const availableVariables = useMemo(() => {
    return localVariables.filter(v => {
      if (mode === 'edit' && v.internalId === initialVariableInternalId) return true
      return !selectedVariableInternalIds.includes(v.internalId)
    })
  }, [localVariables, selectedVariableInternalIds, mode, initialVariableInternalId])

  const handleSave = () => {
    if (!selectedVariable) return
    onSave(selectedVariable.internalId, required)
    onClose()
  }

  const handleVariableCreated = (variable: Variable) => {
    onCreateVariable(variable)
    // Select the newly created variable
    setSelectedVariable(variable)
    setShowVariableCreateDialog(false)
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {mode === 'create' ? 'Add Prefilled Variable' : 'Edit Prefilled Variable'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                options={availableVariables}
                getOptionLabel={(option) => option.internalId}
                value={selectedVariable}
                onChange={(_, newValue) => setSelectedVariable(newValue)}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Variable"
                    required
                    helperText="Choose a local variable to be prefilled externally"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2">{option.internalId}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.variableType === 'multiple' ? 'Array' : 'Single'} • {option.valueType || 'any'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              <IconButton
                onClick={() => setShowVariableCreateDialog(true)}
                color="primary"
                sx={{ mt: 1 }}
                title="Create new variable"
              >
                <AddIcon />
              </IconButton>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                />
              }
              label="Required (must be provided when used in Embedded List)"
            />

            {selectedVariable && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Variable Info:
                </Typography>
                <Typography variant="body2">
                  <strong>{selectedVariable.internalId}</strong> — {selectedVariable.variableType === 'multiple' ? 'Array' : 'Single'} of {selectedVariable.valueType || 'any'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!selectedVariable}>
            {mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variable Create Dialog */}
      <VariableCreateDialog
        open={showVariableCreateDialog}
        title="Create New Variable"
        description="Create a new local variable that can be prefilled externally"
        existingVariables={localVariables.map(v => ({ internalId: v.internalId }))}
        defaultScope="local"
        showScopeSelector={false}
        showValueTypeSelector
        showVariableTypeSelector
        showDefaultValue
        onSave={(variable) => {
          handleVariableCreated(variable)
        }}
        onClose={() => setShowVariableCreateDialog(false)}
      />
    </>
  )
}

export const PrefilledVariablesConfig: FC = () => {
  const { control, watch, setValue, getValues } = useFormContext<MobileListItem>()
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'prefilledVariables',
  })

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const localVariables = watch('variables') || []
  const prefilledVariables = watch('prefilledVariables') || []

  const selectedVariableInternalIds = useMemo(() => {
    return prefilledVariables.map(pv => pv.internalId)
  }, [prefilledVariables])

  const handleAdd = () => {
    setDialogMode('create')
    setEditingIndex(null)
    setDialogOpen(true)
  }

  const handleEdit = (index: number) => {
    setDialogMode('edit')
    setEditingIndex(index)
    setDialogOpen(true)
  }

  const handleDelete = (index: number) => {
    remove(index)
  }

  const handleSave = (variableInternalId: string, required: boolean) => {
    if (dialogMode === 'create') {
      append({ internalId: variableInternalId, required })
    } else if (editingIndex !== null) {
      update(editingIndex, { internalId: variableInternalId, required })
    }
  }

  const handleCreateVariable = (variable: Variable) => {
    // Add the new variable to local variables
    const currentVariables = getValues('variables') || []
    setValue('variables', [...currentVariables, variable])
  }

  const getVariableInfo = (internalId: string) => {
    return localVariables.find(v => v.internalId === internalId)
  }

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2">Prefilled Variables</Typography>
            <Typography variant="caption" color="text.secondary">
              Variables that can be filled from external sources (e.g., Embedded List)
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add
          </Button>
        </Box>

        {fields.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No prefilled variables configured
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {fields.map((field, index) => {
              const pv = prefilledVariables[index]
              const variableInfo = getVariableInfo(pv?.internalId ?? '')

              return (
                <Box
                  key={field.id}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {pv?.internalId}
                      {pv?.required && (
                        <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
                          *
                        </Typography>
                      )}
                    </Typography>
                    {variableInfo && (
                      <Typography variant="caption" color="text.secondary">
                        {variableInfo.variableType === 'multiple' ? 'Array' : 'Single'} • {variableInfo.valueType || 'any'}
                        {pv?.required && ' • Required'}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleEdit(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}

        {localVariables.length === 0 && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            Define local variables first to use them as prefilled variables
          </Typography>
        )}
      </Paper>

      <PrefilledVariableDialog
        open={dialogOpen}
        mode={dialogMode}
        initialVariableInternalId={editingIndex !== null ? prefilledVariables[editingIndex]?.internalId : undefined}
        initialRequired={editingIndex !== null ? prefilledVariables[editingIndex]?.required : undefined}
        localVariables={localVariables}
        selectedVariableInternalIds={selectedVariableInternalIds}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        onCreateVariable={handleCreateVariable}
      />
    </>
  )
}
